import { useCallback, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Box } from '@mui/material';
import { useInViewOnce } from '@/common/presentation/hooks/useInViewOnce';
import {
  A4_ASPECT,
  LIBRARY_BLOCK_BOX_ASPECT,
  LIBRARY_PREVIEW_CANVAS_BG,
  LIBRARY_PREVIEW_LAYOUT_WIDTH_PX,
  LIBRARY_PREVIEW_SHEET_INSET_PX,
  LIBRARY_TEMPLATE_PAGE_FRACTION
} from './libraryPreviewConstants';
import { LibraryPreviewExpand } from './LibraryPreviewExpand';
import { LIBRARY_PREVIEW_PAGE_HEIGHT_PX, previewOpenPlacement, previewRestPlacement, previewSheetScale } from './previewSheetScale';

export type LibraryPreviewFit = 'content' | 'page-top';

export interface LibraryPreviewFrameProps {
  /** The live document render — mounted only once the frame has scrolled into view. */
  children: ReactNode;
  /** `content` (blocks, 4:3 box) or `page-top` (templates, top of an A4 page). Defaults to `content`. */
  fit?: LibraryPreviewFit;
  testId?: string;
  /**
   * Library cards only. Rest stays the top crop; the card's hover/focus rule grows this window
   * to the full page. Leave it off for the Add panel and other static crops.
   */
  hoverExpand?: boolean;
  /** Controls painted inside the expanding window (library cards). Ignored unless `hoverExpand`. */
  overlay?: ReactNode;
}

interface ExpandMetrics {
  width: number;
  openHeight: number;
  sheetHeight: number;
}

function measureExpand(node: HTMLElement): ExpandMetrics {
  const card = node.closest('[data-library-card]');
  const nodeRect = node.getBoundingClientRect();
  const openHeight = card instanceof HTMLElement ? Math.max(0, card.getBoundingClientRect().bottom - nodeRect.top) : 0;
  const sheet = node.querySelector('[data-preview-sheet]');
  const sheetHeight = sheet instanceof HTMLElement ? sheet.scrollHeight : 0;
  return { width: node.clientWidth, openHeight, sheetHeight };
}

function boxAspectRatio(fit: LibraryPreviewFit): number {
  return fit === 'page-top' ? 1 / (A4_ASPECT * LIBRARY_TEMPLATE_PAGE_FRACTION) : LIBRARY_BLOCK_BOX_ASPECT;
}

/**
 * The hero preview of a library card / gallery tile: a cropped window onto a live
 * `DocumentRender`. The sheet is laid out at `LIBRARY_PREVIEW_LAYOUT_WIDTH_PX` and
 * scaled from the top-left to the crop, so the page's own container-fit stays at 1
 * and the ink remains inside the well after that measurement. `hoverExpand` (library
 * cards only) reveals the whole page on hover; per-item ink zoom is not ported.
 *
 * Successful paints stay decorative via the thumbnail (`aria-hidden`); empty and
 * error states are named by the thumbnail so they are not trapped under a hidden
 * ancestor. A card already in the initial viewport mounts immediately.
 */
export function LibraryPreviewFrame({ children, fit = 'content', testId, hoverExpand = false, overlay }: LibraryPreviewFrameProps) {
  const { ref: inViewRef, inView } = useInViewOnce<HTMLDivElement>();
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);
  const [expand, setExpand] = useState<ExpandMetrics>({ width: 0, openHeight: 0, sheetHeight: 0 });

  const ref = useCallback(
    (node: HTMLDivElement | null) => {
      nodeRef.current = node;
      inViewRef(node);
    },
    [inViewRef]
  );

  useLayoutEffect(() => {
    const node = nodeRef.current;
    if (!node) return undefined;

    const apply = () => {
      if (!hoverExpand) {
        const next = previewSheetScale(node.clientWidth);
        setScale((prev) => (Math.abs(prev - next) < 0.001 ? prev : next));
        return;
      }
      const next = measureExpand(node);
      setExpand((prev) =>
        prev.width === next.width && prev.openHeight === next.openHeight && prev.sheetHeight === next.sheetHeight ? prev : next
      );
    };

    apply();

    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(() => apply());
    const watch = (target: Element | null) => {
      if (target) observer.observe(target);
    };
    watch(node);
    watch(hoverExpand ? node.closest('[data-library-card]') : null);
    // The page lays out after the first measure (the sheet is often 0px until the
    // document paints). Watching the sheet picks up that height so the opened
    // placement contains the real page instead of the fallback.
    watch(hoverExpand ? node.querySelector('[data-preview-sheet]') : null);
    return () => observer.disconnect();
  }, [inView, hoverExpand]);

  const restHeightPx = expand.width > 0 ? expand.width / boxAspectRatio(fit) : 0;
  const openHeightPx = expand.openHeight > 0 ? expand.openHeight : restHeightPx;
  const rest = previewRestPlacement(expand.width);
  const open = previewOpenPlacement(
    expand.width,
    openHeightPx,
    expand.sheetHeight > 0 ? expand.sheetHeight : LIBRARY_PREVIEW_PAGE_HEIGHT_PX
  );

  return (
    <Box
      ref={ref}
      data-testid={testId}
      data-state={inView ? 'ready' : 'loading'}
      sx={{
        position: 'relative',
        width: '100%',
        // The crop sits above the full-card link. Pointer events pass through it; the hover
        // controls opt back in when they are revealed.
        pointerEvents: hoverExpand ? 'none' : 'auto',
        aspectRatio: boxAspectRatio(fit),
        overflow: hoverExpand ? 'visible' : 'hidden',
        backgroundColor: hoverExpand ? 'transparent' : LIBRARY_PREVIEW_CANVAS_BG,
        borderRadius: 'var(--border-radius-none)'
      }}
    >
      {inView && hoverExpand ? (
        <LibraryPreviewExpand
          testId={testId}
          restHeightPx={restHeightPx}
          openHeightPx={openHeightPx}
          rest={rest}
          open={open}
          overlay={overlay}
        >
          {children}
        </LibraryPreviewExpand>
      ) : null}
      {!inView ? (
        <Box
          aria-hidden
          data-testid={testId ? `${testId}:placeholder` : undefined}
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'var(--surface-neutral-container-secondary)',
            '@media (prefers-reduced-motion: no-preference)': {
              animation: 'library-preview-pulse 1.6s ease-in-out infinite'
            },
            '@keyframes library-preview-pulse': {
              '0%, 100%': { opacity: 0.6 },
              '50%': { opacity: 0.35 }
            }
          }}
        />
      ) : null}
      {inView && !hoverExpand ? (
        <Box
          data-testid={testId ? `${testId}:sheet` : undefined}
          data-anchor="top-left"
          data-layout-width={LIBRARY_PREVIEW_LAYOUT_WIDTH_PX}
          sx={{
            position: 'absolute',
            top: `${LIBRARY_PREVIEW_SHEET_INSET_PX}px`,
            left: `${LIBRARY_PREVIEW_SHEET_INSET_PX}px`,
            width: LIBRARY_PREVIEW_LAYOUT_WIDTH_PX,
            transform: `scale(${scale})`,
            transformOrigin: 'top left'
          }}
        >
          {children}
        </Box>
      ) : null}
    </Box>
  );
}
